import { useCallback, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

// Note: the profiling job has two parameters, low_card_threshold and high_card_threshold,
// but they are optional and have defaults set in the backend.
// Leaving them out for now as there's as yet no UI to set them.
export interface StartProfilingParams {
  sourceFile: string;
}

export interface ProfilingResult {
  ingestionId: string;
  profilingJobId: string;
}

/**
 * Hook to start file profiling job.
 *
 * This hook handles starting profiling job (column types, cardinality, etc.)
 *
 * This job will produce a single file ([file]._TABULAR_FILE_PROFILE.json)
 * It should contain:
 * - task_name: "TabularProfileTaskMessage"
 * - num_rows
 * - num_columns
 * - column_profile for each column:
 *  - column_name
 *  - data_type
 *  - cardinality:
 *    - cardinality_count
 *    - cardinality_category
 *
 */
export const useStartProfiling = () => {
  const { datasetId } = useParams();
  const [searchParams] = useSearchParams();
  const ingestionId = searchParams.get("ingestion");
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startProfiling = useCallback(
    async ({ sourceFile }: StartProfilingParams): Promise<ProfilingResult | null> => {
      if (!datasetId) {
        const errorMsg = "Dataset ID is required";
        setError(errorMsg);
        return null;
      }

      if (!ingestionId) {
        const errorMsg = "Ingestion ID is required";
        setError(errorMsg);
        return null;
      }

      if (!sourceFile) {
        const errorMsg = "Platform storage key is required";
        setError(errorMsg);
        return null;
      }

      setIsStarting(true);
      setError(null);

      try {
        // Start profiling job
        const profilingJobResp = await fetch(
          `${VITE_CATALOG_API}/datasets/${datasetId}/ingestions/${ingestionId}/jobs`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              job_type: "profiling",
              output_path: `ingestions/${datasetId}/${ingestionId}`,
              arguments: {
                file_uris: [sourceFile],
              },
            }),
          }
        );

        if (!profilingJobResp.ok) {
          const errorText = await profilingJobResp.text();
          throw new Error(`Failed to start profiling job: ${errorText}`);
        }

        // Backend returns plain text with quotes, need to strip them
        let profilingJobId = await profilingJobResp.text();
        profilingJobId = profilingJobId.replace(/^"|"$/g, ""); // Remove leading/trailing quotes

        trackEvent("file_profiling_started", {
          dataset_id: datasetId,
          ingestion_id: ingestionId,
          profiling_job_id: profilingJobId,
          source_file: sourceFile,
        });

        return {
          ingestionId,
          profilingJobId,
        };
      } catch (err: any) {
        const errorMsg = err.message || "Failed to start profiling. Please try again.";
        console.error("Failed to start profiling:", err);
        rumClient?.recordError(err);
        setError(errorMsg);
        return null;
      } finally {
        setIsStarting(false);
      }
    },
    [datasetId, ingestionId]
  );

  const clearError = () => setError(null);

  return {
    startProfiling,
    isStarting,
    error,
    clearError,
  };
};
